<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import Translator from "@/utils/common/Translator.ts";
import ChatUtils from "@/utils/common/ChatUtils.ts";
import ChatMediaMenu from "@/components/navBar/chat/ChatMediaMenu.vue";
import {MessageMetadata} from "@/utils/presenters/interfaces.ts";
import {useQuasar} from "quasar";

export interface PreviewItem {
  src: string;
  fileName: string;
  isVideo: boolean;
}

const visible = defineModel<boolean>({required: true});

const props = defineProps<{
  items: PreviewItem[];
  startIndex?: number;
}>();

const emit = defineEmits<{
  close: [];
}>();

const $q = useQuasar();
const isMobile = computed(() => $q.screen.lt.sm);

const currentIndex = defineModel<number>('currentIndex', {default: 0});
const currentItem = computed<PreviewItem | undefined>(() => props.items[currentIndex.value]);
const hasMultiple = computed(() => props.items.length > 1);

function close() {
  visible.value = false;
  emit('close');
}

function prevSlide() {
  if (currentIndex.value > 0) currentIndex.value--;
}

function nextSlide() {
  if (currentIndex.value < props.items.length - 1) currentIndex.value++;
}

// --- Keyboard navigation ---

function onKeydown(e: KeyboardEvent) {
  if (!visible.value) return;
  const isRtl = Translator.isLanguageRTL();
  if (e.key === 'ArrowLeft') {
    e.preventDefault();
    isRtl ? nextSlide() : prevSlide();
  } else if (e.key === 'ArrowRight') {
    e.preventDefault();
    isRtl ? prevSlide() : nextSlide();
  } else if (e.key === 'Escape') {
    close();
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

const touchStartX = ref(0);
const touchStartY = ref(0);
const SWIPE_THRESHOLD = 50;

function onTouchStart(e: TouchEvent) {
  if (!hasMultiple.value) return;
  touchStartX.value = e.touches[0].clientX;
  touchStartY.value = e.touches[0].clientY;
}

function onTouchEnd(e: TouchEvent) {
  if (!hasMultiple.value) return;
  const deltaX = e.changedTouches[0].clientX - touchStartX.value;
  const deltaY = e.changedTouches[0].clientY - touchStartY.value;
  if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaY) > Math.abs(deltaX)) return;
  const isRtl = Translator.isLanguageRTL();
  if (deltaX > 0) {
    isRtl ? nextSlide() : prevSlide();
  } else {
    isRtl ? prevSlide() : nextSlide();
  }
}

function buildSingleItem(src: string, fileName: string): PreviewItem[] {
  const ext = ChatUtils.getFileExtension(fileName);
  return [{src, fileName, isVideo: ChatUtils.VIDEO_EXTENSIONS.has(ext)}];
}

function buildAlbumItems(messages: MessageMetadata[]): PreviewItem[] {
  return messages.map(msg => {
    const fileId = msg.attachment?.attachmentInfo?.fileId;
    const fileName = msg.attachment?.attachmentInfo?.fileName ?? '';
    const ext = ChatUtils.getFileExtension(fileName);
    return {
      src: ChatUtils.getFileDownloadUrl(fileId),
      fileName,
      isVideo: ChatUtils.VIDEO_EXTENSIONS.has(ext),
    };
  });
}

defineExpose({buildSingleItem, buildAlbumItems});
</script>

<template>
  <q-dialog v-model="visible" maximized transition-show="fade" transition-hide="fade">
    <div class="tw-w-full tw-h-full tw-bg-black/70 tw-flex tw-flex-col tw-items-center tw-justify-center tw-relative"
         @click="close" @touchstart.passive="onTouchStart" @touchend.passive="onTouchEnd">
      <div class="tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-z-10">
        <div class="tw-text-white tw-text-sm tw-opacity-80">
          <span v-text="currentItem?.fileName"/>
          <span v-if="hasMultiple" class="tw-ml-2 tw-opacity-60" v-text="currentIndex + 1 + '/' + items.length"/>
        </div>
        <div class="tw-flex tw-items-center tw-gap-1">
          <ChatMediaMenu v-if="currentItem" :download-url="currentItem.src" btn-color="white"/>
          <q-btn flat round icon="close" color="white" size="sm" @click.stop="close"/>
        </div>
      </div>
      <q-btn v-if="hasMultiple && currentIndex > 0 && !isMobile" flat round
             :icon="Translator.isLanguageRTL() ? 'chevron_right' : 'chevron_left'" color="white" size="lg"
             class="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-z-10 tw-bg-black/30 hover:tw-bg-black/50"
             @click.stop="prevSlide"/>
      <template v-if="currentItem">
        <video v-if="currentItem.isVideo" :key="currentItem.src" :src="currentItem.src" controls autoplay
               class="tw-max-w-[90vw] tw-max-h-[85vh] tw-object-contain" @click.stop/>
        <img v-else :key="currentItem.src" :src="currentItem.src" :alt="currentItem.fileName"
             class="tw-max-w-[90vw] tw-max-h-[85vh] tw-object-contain" @click.stop/>
      </template>
      <q-btn v-if="hasMultiple && currentIndex < items.length - 1 && !isMobile" flat round
             :icon="Translator.isLanguageRTL() ? 'chevron_left' : 'chevron_right'" color="white" size="lg"
             class="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-z-10 tw-bg-black/30 hover:tw-bg-black/50"
             @click.stop="nextSlide"/>
      <div v-if="hasMultiple" class="tw-absolute tw-bottom-4 tw-flex tw-gap-1.5 tw-z-10" @click.stop>
        <button v-for="(_item, i) in items" :key="i" @click="currentIndex = i"
                class="tw-rounded-full tw-border-none tw-cursor-pointer tw-transition-all"
                :class="[ i === currentIndex ? 'tw-bg-white tw-scale-125' : 'tw-bg-white/40 hover:tw-bg-white/70',
                  isMobile ? 'tw-w-2.5 tw-h-2.5' : 'tw-w-2 tw-h-2']"/>
      </div>
    </div>
  </q-dialog>
</template>
